import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "./contexts/LanguageContext";
import { useLanguage } from "./contexts/LanguageContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import Landing from "./pages/Landing";
import Shop from "./pages/Shop";
import Cart from "./pages/Cart";
import Dashboard from "./pages/DashboardV2";
import Admin from "./pages/Admin";
import Messages from "./pages/Messages";
import TopUp from "./pages/TopUp";
import Support from "./pages/Support";
import Terms from "./pages/Terms";
import Warranty from "./pages/Warranty";
import Privacy from "./pages/Privacy";
import FAQ from "./pages/FAQ";
import Login from "./pages/Login";
import Blocked from "./pages/Blocked";
import NotFound from "./pages/NotFound";
import Welcome from "./pages/Welcome";
import Contact from "./pages/Contact";
import Coupon from "./pages/Coupon";
import PaymentCancelled from "./pages/PaymentCancelled";
import AllpaySuccess from "./pages/AllpaySuccess";
import Test2 from "./pages/Test2";
import { AccessibilityWidget } from "./components/AccessibilityWidget";
import { CartSidebar } from "./components/CartSidebar";
import SupportWidget from "./components/SupportWidget";
import SEOHead from "./components/SEOHead";
import { Toaster } from "sonner";
import { useState, useEffect, useRef, useLayoutEffect, useCallback } from "react";

function AdminRedirect() {
  useEffect(() => { window.location.replace("https://admin.ifollowers.site"); }, []);
  return null;
}
import { useLocation } from "react-router-dom";
import { Info, AlertTriangle, AlertCircle, CheckCircle, Sparkles, X, ExternalLink } from "lucide-react";

const GLOBAL_BANNER_STYLES = {
  info:    { bg: "#405DE6", Icon: Info },
  warning: { bg: "#F77737", Icon: AlertTriangle },
  error:   { bg: "#E1306C", Icon: AlertCircle },
  success: { bg: "#22c55e", Icon: CheckCircle },
  promo:   { bg: "#8B5CF6", Icon: Sparkles },
};

function GlobalBanners() {
  const { lang } = useLanguage();
  const location = useLocation();
  const [banners, setBanners] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dismissed_banners") || "[]"); } catch { return []; }
  });
  const [spacerHeight, setSpacerHeight] = useState(0);
  const bannerRef = useRef(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site/banners`)
      .then(r => r.ok ? r.json() : [])
      .then(d => setBanners(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  const dismiss = (id) => {
    const next = [...dismissed, id];
    setDismissed(next);
    localStorage.setItem("dismissed_banners", JSON.stringify(next));
  };

  const visible = banners.filter(b => !dismissed.includes(b.banner_id));

  // Measure fixed banner height and keep spacer in sync
  useLayoutEffect(() => {
    if (!bannerRef.current) { setSpacerHeight(0); return; }
    const measure = () => setSpacerHeight(bannerRef.current ? bannerRef.current.offsetHeight : 0);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(bannerRef.current);
    return () => ro.disconnect();
  }, [visible.length]);

  // Never show on admin page — no banner, no spacer
  if (location.pathname === "/admin") return null;

  if (!visible.length) return null;

  const L = (obj) => (obj || {})[lang] || (obj || {}).en || (obj || {}).ru || (obj || {}).he || "";

  return (
    <>
      {/* In-flow spacer: pushes page content down to match fixed banner height */}
      <div aria-hidden="true" style={{ height: spacerHeight, flexShrink: 0 }} />

      {/* Fixed visual banner — sits below navbar, does not overlap content */}
      <div ref={bannerRef} className="fixed left-0 right-0 z-40" style={{ top: "56px" }}>
        {visible.map(b => {
          const s = GLOBAL_BANNER_STYLES[b.type] || GLOBAL_BANNER_STYLES.info;
          const { Icon } = s;
          return (
            <div key={b.banner_id} className="w-full flex items-center gap-3 px-5 py-2.5"
              style={{ background: s.bg, borderBottom: "1px solid rgba(0,0,0,0.15)" }}>
              <Icon className="w-4 h-4 flex-shrink-0 text-white opacity-90" />
              <span className="flex-1 text-sm font-medium text-center leading-snug text-white">
                {L(b.message)}
                {b.link_url && (
                  <a href={b.link_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 font-bold ms-2 underline underline-offset-2 hover:opacity-80">
                    {L(b.link_label) || "→"} <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </span>
              {b.dismissible !== false && (
                <button onClick={() => dismiss(b.banner_id)} className="flex-shrink-0 hover:opacity-70 transition-opacity text-white">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

function RefCapture() {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");
    if (ref) localStorage.setItem("pending_ref", ref);
  }, [location.search]);
  return null;
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getDeviceType() {
  const ua = navigator.userAgent;
  if (/mobile/i.test(ua)) return "mobile";
  if (/tablet|ipad/i.test(ua)) return "tablet";
  return "desktop";
}
function getOrCreateSessionId() {
  let sid = sessionStorage.getItem("_asid");
  if (!sid) {
    sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem("_asid", sid);
  }
  return sid;
}

function ActivityPing() {
  const location = useLocation();
  const { user } = useAuth();
  useEffect(() => {
    const page = location.pathname + location.hash;
    const referrer = document.referrer || "";
    const device_type = getDeviceType();
    if (user) {
      fetch(`${API}/user/ping`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ page, referrer, device_type }),
      }).catch(() => {});
    } else {
      const session_id = getOrCreateSessionId();
      fetch(`${API}/analytics/track`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ page, referrer, device_type, session_id }),
      }).catch(() => {});
    }
  }, [location.pathname, location.hash, user]);
  return null;
}

function ClickTracker() {
  const location = useLocation();
  useEffect(() => {
    const session_id = getOrCreateSessionId();
    const handleClick = (e) => {
      const target = e.target.closest("a[href], button, [data-track]");
      if (!target) return;
      const element = (
        target.dataset.track ||
        target.getAttribute("href") ||
        target.textContent?.trim().slice(0, 60) ||
        target.tagName
      );
      if (!element || element.length < 2) return;
      fetch(`${API}/analytics/click`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ element, page: location.pathname, session_id }),
      }).catch(() => {});
    };
    document.addEventListener("click", handleClick, { passive: true, capture: true });
    return () => document.removeEventListener("click", handleClick, { capture: true });
  }, [location.pathname]);
  return null;
}

function ScrollRevealInit() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.07, rootMargin: "0px 0px -30px 0px" }
    );
    const observe = () => {
      document.querySelectorAll(".scroll-reveal:not(.visible)").forEach((el) => observer.observe(el));
    };
    observe();
    const mutation = new MutationObserver(observe);
    mutation.observe(document.body, { childList: true, subtree: true });
    return () => { observer.disconnect(); mutation.disconnect(); };
  });
  return null;
}

function AppRouter() {
  const location = useLocation();
  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead />
      <RefCapture />
      <ActivityPing />
      <ClickTracker />
      <Navbar />
      <GlobalBanners />
      <div className="flex-1">
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/blocked" element={<Blocked />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/cart" element={<ProtectedRoute><Cart /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
          <Route path="/tasks" element={<Navigate to="/dashboard" replace />} />
          <Route path="/topup" element={<ProtectedRoute><TopUp /></ProtectedRoute>} />
          <Route path="/support" element={<ProtectedRoute><Support /></ProtectedRoute>} />
          <Route path="/welcome" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute adminOnly><AdminRedirect /></ProtectedRoute>} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/warranty" element={<Warranty />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/coupon/:code" element={<Coupon />} />
          <Route path="/payment-cancelled" element={<PaymentCancelled />} />
          <Route path="/allpay-success" element={<AllpaySuccess />} />
          <Route path="/test2" element={<ProtectedRoute><Test2 /></ProtectedRoute>} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
      <Footer />
      <CartSidebar />
      <SupportWidget />
      {location.pathname !== "/admin" && <AccessibilityWidget />}
      <ScrollRevealInit />
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster
            position="top-center"
            richColors
            closeButton={false}
            swipeToDismissDirection="up"
            expand={false}
            visibleToasts={4}
            toastOptions={{
              duration: 3600,
              style: {
                background: "var(--bg-secondary)",
                border: "1px solid var(--border-color)",
                color: "var(--text-primary)",
                borderRadius: "16px",
                boxShadow: "0 16px 48px rgba(0,0,0,0.18)",
              },
            }}
          />
        </CartProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
